/**
 * Hook personalizado para conectar con WebSocket de comprobantes.
 *
 * Proporciona actualizaciones en tiempo real sin necesidad de refresh.
 *
 * Refactor 2026-05-01 — estabilidad de la conexión:
 *   - Los callbacks (`onNewReceipt`, `onReceiptReconciled`) se almacenan en refs
 *     para que el parent pueda pasar funciones nuevas sin reiniciar el WebSocket.
 *   - `reconnectAttempts` sale del state y se maneja en ref: antes, cada intento
 *     disparaba un re-render + re-ejecución del useEffect → cleanup → nuevo connect,
 *     mientras el `setTimeout(connect)` del onclose anterior seguía vivo. Eso
 *     generaba conexiones duplicadas y loops.
 *   - El cleanup desregistra `onclose` antes de `close()` y marca `unmounted`
 *     para que ningún timeout pendiente relance `connect()` tras el unmount.
 *   - Resultado: el WS solo se reinicia si cambia `clienteId` (comportamiento
 *     correcto). Los comprobantes en vivo dejan de perderse.
 */
import { useEffect, useRef, useState } from 'react';

const useReceiptsWebSocket = (clienteId, onNewReceipt, onReceiptReconciled) => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  // Callbacks en refs: que el parent recree las funciones no dispara reconexión.
  const onNewReceiptRef = useRef(onNewReceipt);
  const onReceiptReconciledRef = useRef(onReceiptReconciled);
  useEffect(() => { onNewReceiptRef.current = onNewReceipt; }, [onNewReceipt]);
  useEffect(() => { onReceiptReconciledRef.current = onReceiptReconciled; }, [onReceiptReconciled]);

  // Contadores / flags que NO deben disparar re-render ni re-suscripción.
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    if (!clienteId) {
      return;
    }

    unmountedRef.current = false;
    reconnectAttemptsRef.current = 0;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/receipts/${clienteId}`;

    console.log(`🔌 Conectando WebSocket para cliente ${clienteId}...`);

    const connect = () => {
      if (unmountedRef.current) return;
      try {
        const socket = new WebSocket(wsUrl);
        ws.current = socket;

        socket.onopen = () => {
          if (unmountedRef.current) { socket.close(); return; }
          console.log(`✅ WebSocket conectado para cliente ${clienteId}`);
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;

          // Ping keepalive cada 30 segundos
          const pingInterval = setInterval(() => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send('ping');
            }
          }, 30000);
          socket.pingInterval = pingInterval;
        };

        socket.onmessage = (event) => {
          if (event.data === 'pong') return;
          try {
            const data = JSON.parse(event.data);
            console.log('📨 Mensaje WebSocket recibido:', data.type);

            if (data.type === 'new_receipt') {
              console.log('🆕 Nuevo comprobante recibido:', data.receipt.id);
              const cb = onNewReceiptRef.current;
              if (cb) {
                cb({
                  receipt: data.receipt,
                  balance_anterior: data.balance_anterior,
                  balance_nuevo: data.balance_nuevo,
                });
              }
            }

            if (data.type === 'receipt_reconciled') {
              console.log('✅ Comprobante conciliado en tiempo real:', data.receipt.id);
              const cb = onReceiptReconciledRef.current;
              if (cb) {
                cb({
                  receipt: data.receipt,
                  reconciliation_type: data.reconciliation_type,
                  notification_app: data.notification_app,
                });
              }
            }
          } catch (error) {
            console.error('Error parseando mensaje WebSocket:', error);
          }
        };

        socket.onerror = (error) => {
          console.error('❌ Error en WebSocket:', error);
        };

        socket.onclose = () => {
          if (socket.pingInterval) clearInterval(socket.pingInterval);
          setIsConnected(false);
          if (unmountedRef.current) return;

          console.log('❌ WebSocket desconectado');
          // Backoff exponencial, cap 30s
          const attempt = reconnectAttemptsRef.current;
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          reconnectAttemptsRef.current = attempt + 1;
          console.log(`🔄 Reconectando en ${delay / 1000}s... (intento ${attempt + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!unmountedRef.current) connect();
          }, delay);
        };
      } catch (error) {
        console.error('Error creando WebSocket:', error);
      }
    };

    connect();

    // Cleanup al desmontar o al cambiar clienteId: desactiva handlers y cancela
    // timeouts pendientes para que no haya reconexión fantasma.
    return () => {
      console.log(`🔌 Desconectando WebSocket para cliente ${clienteId}`);
      unmountedRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      const socket = ws.current;
      if (socket) {
        if (socket.pingInterval) clearInterval(socket.pingInterval);
        // Desregistramos onclose ANTES de cerrar para que el cierre voluntario
        // no programe un nuevo reconnect.
        socket.onclose = null;
        socket.onerror = null;
        socket.onmessage = null;
        socket.onopen = null;
        try { socket.close(); } catch (_) { /* noop */ }
        ws.current = null;
      }
    };
    // Solo `clienteId` dispara reconexión. Los callbacks viven en refs.
  }, [clienteId]);

  return { isConnected };
};

export default useReceiptsWebSocket;

// cache bust v1777670154
